import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedModule } from '@shared/shared.module';
import { TsButtonModule, TSIconModule, TsInputComponent, TsSnackbarService } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';

import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

import { CategoryAccountUtils } from '../../utils/category-account.utils';
import { CategoryAccountRequest } from '../../models';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AssetManagementState } from '../../state/asset-management.state';
import { noop } from 'rxjs';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementMessages } from '../../utils/asset-management-messages.utils';
import { AssetManagementLabels } from '../../enums/asset-management-reports.enum';

export type CategoryAccountDialogData = CategoryAccountRequest;

@Component({
  selector: 'app-category-account-management-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    SharedModule,
    TSIconModule,
    TsButtonModule,
    TsDialogComponent,
    TsInputComponent,
    TsSelectModule,
    TsSkeletonComponent,
  ],
  templateUrl: './category-account-management-dialog.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryAccountManagementDialogComponent implements OnInit {
  protected readonly dialogRef = inject(DialogRef);
  protected readonly Validators = Validators;
  protected readonly CategoryAccountUtils = CategoryAccountUtils;
  readonly data = inject<CategoryAccountDialogData>(DIALOG_DATA);
  readonly fb = inject(FormBuilder);
  readonly store = inject(Store);
  readonly destroyRef = inject(DestroyRef);
  readonly snackbar = inject(TsSnackbarService);
  readonly nullCategoryOption = { value: -1, label: 'None' };

  get title(): string {
    return this.data?.name ? 'Edit Account' : 'Add Account';
  }

  get categoryAccountFormControls() {
    return {
      name: this.categoryAccountForm.get('name'),
      description: this.categoryAccountForm.get('description'),
      parent_id: this.categoryAccountForm.get('parent_id'),
      normal_balance: this.categoryAccountForm.get('normal_balance'),
      report_type: this.categoryAccountForm.get('report_type'),
      account_type: this.categoryAccountForm.get('account_type'),
    };
  }

  get isSubmitable() {
    if (this.categoryAccountFormControls.account_type.value === 'total') {
      return (
        this.categoryAccountForm.valid &&
        this.totalAggregatesFormArray.valid &&
        (this.totalAggregatesFormArray.dirty || this.categoryAccountForm.dirty)
      );
    }

    return this.categoryAccountForm.valid && this.categoryAccountForm.dirty;
  }

  categoryAccountForm: any = this.fb.group({
    id: [this.data?.id || null],
    name: [this.data?.name || '', Validators.required],
    description: [this.data?.description || ''],
    // TODO add validation when db is seeded with root-level account categories
    parent_id: [this.data?.parent_id || this.nullCategoryOption.value, Validators.required],
    normal_balance: [this.data?.normal_balance || null, Validators.required],
    report_type: [this.data?.report_type || null, Validators.required],
    account_type: [this.data?.account_type || 'group', Validators.required],
  });
  totalAggregatesFormArray = this.fb.array(this.populateTotalAggregatesForm(this.data?.total_aggregates || []));

  readonly categoryAccountOptions$ = this.store.select(AssetManagementState.categoryAccountOptions).pipe(
    map((options) => options.filter((option) => option.value !== this.data?.id)),
    map((options) => [{ ...this.nullCategoryOption }, ...options]),
  );

  ngOnInit() {
    this.store
      .dispatch(new AssetManagementActions.LoadCategoryAccounts())
      .pipe(
        take(1),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          null,
          AssetManagementMessages.Error.LOAD(AssetManagementLabels.CategoryAccounts),
        ),
      )
      .subscribe(noop);

    this.categoryAccountFormControls.report_type.valueChanges.subscribe(() => {
      // Reset total aggregates form array when report type changes
      this.totalAggregatesFormArray.clear();
    });
  }

  submitForm(): void {
    if (!this.isSubmitable) return;

    const { parent_id } = {
      parent_id:
        +this.categoryAccountFormControls.parent_id.value !== +this.nullCategoryOption.value
          ? +this.categoryAccountFormControls.parent_id.value
          : null,
    };

    if (this.categoryAccountFormControls.account_type.value === 'total') {
      this.dialogRef.close({
        ...this.categoryAccountForm.value,
        parent_id,
        total_aggregates: this.totalAggregatesFormArray.value.map((value) => +value),
      });
    } else {
      this.dialogRef.close({ ...this.categoryAccountForm.value, parent_id });
    }
  }

  private populateTotalAggregatesForm(categoryIds: number[]): FormControl[] {
    return categoryIds.map((number) => this.fb.control(number, Validators.required));
  }

  addAggregatedAccountForm() {
    this.totalAggregatesFormArray.push(this.fb.control(null, Validators.required));
  }

  removeAggregatedAccountForm(index: number) {
    this.totalAggregatesFormArray.removeAt(index);
    this.totalAggregatesFormArray.markAsDirty();
  }

  filterPreselectedCategories(options: { value: string }[], currentValue: string): any[] {
    const preselectedCategoryIds = this.totalAggregatesFormArray.controls.map((control) => control.value);

    return options.filter(({ value }) => currentValue === value || !preselectedCategoryIds.includes(value));
  }
}
